// ╔══════════════════════════════════════════════════════════════╗
// ║  Helix 2000 — Development Configuration                      ║
// ║  Centralized config for feature toggles, debug flags,        ║
// ║  and development environment settings.                       ║
// ╠══════════════════════════════════════════════════════════════╣
// ║  CONFIG HIERARCHY (most specific wins):                       ║
// ║                                                               ║
// ║  1. DevConfig.systems.* (this file)                           ║
// ║     → Preset-based. Controls whether a system CLASS is        ║
// ║       instantiated at boot (e.g., multiplayer, editor, hud).  ║
// ║     → Set via VITE_PRESET env var or ?preset= URL param.     ║
// ║                                                               ║
// ║  2. environment.ts → getEnvironmentConfig()                   ║
// ║     → Runtime env detection (dev/prod), auto-login, URLs.     ║
// ║     → runtime.config.ts re-exports API_URL / WS_URL from it. ║
// ║                                                               ║
// ║  3. UIContext defaults (React)                                 ║
// ║     → Per-panel visibility (showChat, showMultiplayerDiag…).  ║
// ║     → Only relevant if the parent system is enabled above.    ║
// ╚══════════════════════════════════════════════════════════════╝

import { bootParam, bootSearch } from '../boot/bootUrl';
export type DevPresetName =
  | 'minimal'
  | 'editor'
  | 'gameplay'
  | 'multiplayer'
  | 'full'
  | 'e2e'
  | 'arena'
  | 'demo'
  | 'abilities'
  | '1v1-pig'
  | 'live-lobby'
  | 'sim-spectator'
  | 'debug-tools-only'
  | 'combat-lab';

export type MobLabelMode = 'full' | 'hidden';

export interface DevSystemsConfig {
  multiplayer: boolean;
  npcs: boolean;
  mobs: boolean;
  combat: boolean;
  particles: boolean;
  parallax: boolean;
  vfx: boolean;
  simulation: boolean;
  ui: boolean;
  hud: boolean;
  chat: boolean;
  actionBar: boolean;
  mapBanner: boolean;
  playerList: boolean;
  debugToolbox: boolean;
  quickMenu: boolean;
  targetInfo: boolean;
  connectionStatus: boolean;
  editor: boolean;
  inspector: boolean;
  debugOverlay: boolean;
  miniStatsDisplay: boolean;
  playerInfoPanel: boolean;
  allowAnyAbilityEquip: boolean;
}

export interface DevPresentationConfig {
  mobLabelMode: MobLabelMode;
  showMobHealthBars: boolean;
  /**
   * Render the overhead HP/resource stack for player characters, including the
   * local character. Kept separate from `showMobHealthBars` so a clean mob
   * presentation never silently hides player vitals. Default: true.
   */
  showPlayerHealthBars: boolean;
  /**
   * Show the countdown label left at a dead mob's spawn point. This is useful
   * for spawn diagnostics, but reads like debug noise during normal play.
   * Default: false.
   */
  showMobRespawnTimers: boolean;
  multiplayerLabelsOverride: boolean | null;
  cameraZoom: number | null;
  /**
   * Chat panel starts expanded (true) or collapsed-to-badge (false).
   * Combat log + system messages always pump into chat regardless — this
   * just controls initial visibility. true = combat events visible
   * immediately on join, no click-the-badge step. Default: true.
   */
  chatDefaultExpanded: boolean;
  /**
   * Render numeric "current/max" text inline on overhead HP bars
   * (mobs + remote players). When true, every entity shows e.g. "65/100"
   * over their head so DJ doesn't have to read color gradient + width
   * to gauge HP. Default: true. See `src/game/rendering/utils/BarLabel.ts`.
   */
  showHealthBarNumbers: boolean;
  /**
   * Render numeric "current/max" text inline on overhead resource (mana
   * / energy) bars. Same UX intent as `showHealthBarNumbers`, applies to
   * remote players' MP bars. Default: true.
   */
  showResourceBarNumbers: boolean;
}

const PRESET_SYSTEMS: Record<DevPresetName, DevSystemsConfig> = {
  minimal: {
    multiplayer: false, npcs: false, mobs: false, combat: false,
    particles: false, parallax: false, vfx: false, simulation: false,
    ui: false, hud: false, chat: false, actionBar: false,
    mapBanner: false, playerList: false, debugToolbox: false,
    quickMenu: false, targetInfo: false, connectionStatus: false,
    editor: false, inspector: false, debugOverlay: false,
    miniStatsDisplay: false, playerInfoPanel: false,
    allowAnyAbilityEquip: false,
  },
  editor: {
    // Multiplayer + chat are ON so the user can use /spawnmob to drop mobs
    // into the live room state from the editor session. The editor preset
    // is the canonical "single-player + edit" QA flow.
    multiplayer: true, npcs: true, mobs: true, combat: true,
    particles: true, parallax: false, vfx: true, simulation: false,
    ui: true, hud: true, chat: true, actionBar: true,
    mapBanner: false, playerList: false, debugToolbox: true,
    quickMenu: true, targetInfo: true, connectionStatus: false,
    editor: true, inspector: false, debugOverlay: false,
    miniStatsDisplay: false, playerInfoPanel: true,
    allowAnyAbilityEquip: true,
  },
  gameplay: {
    multiplayer: false, npcs: true, mobs: true, combat: true,
    particles: true, parallax: true, vfx: true, simulation: false,
    ui: true, hud: false, chat: true, actionBar: true,
    mapBanner: true, playerList: false, debugToolbox: true,
    quickMenu: false, targetInfo: true, connectionStatus: false,
    editor: true, inspector: false, debugOverlay: false,
    miniStatsDisplay: false, playerInfoPanel: true,
    allowAnyAbilityEquip: true,
  },
  multiplayer: {
    multiplayer: true, npcs: true, mobs: true, combat: true,
    particles: true, parallax: true, vfx: true, simulation: false,
    ui: true, hud: true, chat: true, actionBar: true,
    mapBanner: true, playerList: true, debugToolbox: true,
    quickMenu: true, targetInfo: true, connectionStatus: true,
    editor: false, inspector: false, debugOverlay: true,
    miniStatsDisplay: true, playerInfoPanel: true,
    allowAnyAbilityEquip: false,
  },
  full: {
    multiplayer: true, npcs: true, mobs: true, combat: true,
    particles: true, parallax: true, vfx: true, simulation: true,
    ui: true, hud: true, chat: true, actionBar: true,
    mapBanner: true, playerList: true, debugToolbox: true,
    quickMenu: true, targetInfo: true, connectionStatus: true,
    editor: true, inspector: true, debugOverlay: true,
    miniStatsDisplay: true, playerInfoPanel: true,
    allowAnyAbilityEquip: true,
  },
  e2e: {
    multiplayer: true, npcs: true, mobs: true, combat: true,
    particles: true, parallax: true, vfx: true, simulation: false,
    ui: false, hud: false, chat: false, actionBar: false,
    mapBanner: false, playerList: false, debugToolbox: false,
    quickMenu: false, targetInfo: false, connectionStatus: false,
    editor: false, inspector: false, debugOverlay: false,
    miniStatsDisplay: false, playerInfoPanel: false,
    allowAnyAbilityEquip: false,
  },
  arena: {
    multiplayer: false, npcs: true, mobs: true, combat: true,
    particles: true, parallax: false, vfx: true, simulation: false,
    ui: true, hud: true, chat: false, actionBar: true,
    mapBanner: false, playerList: false, debugToolbox: true,
    quickMenu: true, targetInfo: true, connectionStatus: false,
    editor: false, inspector: false, debugOverlay: false,
    miniStatsDisplay: true, playerInfoPanel: true,
    allowAnyAbilityEquip: true,
  },
  demo: {
    multiplayer: true, npcs: true, mobs: true, combat: true,
    particles: true, parallax: true, vfx: true, simulation: false,
    ui: true, hud: true, chat: false, actionBar: true,
    mapBanner: false, playerList: false, debugToolbox: false,
    quickMenu: true, targetInfo: true, connectionStatus: false,
    editor: false, inspector: false, debugOverlay: false,
    miniStatsDisplay: false, playerInfoPanel: true,
    allowAnyAbilityEquip: true,
  },
  abilities: {
    multiplayer: false, npcs: true, mobs: true, combat: true,
    particles: true, parallax: true, vfx: true, simulation: false,
    ui: true, hud: true, chat: false, actionBar: true,
    mapBanner: false, playerList: false, debugToolbox: true,
    quickMenu: true, targetInfo: true, connectionStatus: false,
    editor: true, inspector: false, debugOverlay: false,
    miniStatsDisplay: false, playerInfoPanel: true,
    allowAnyAbilityEquip: true,
  },
  '1v1-pig': {
    multiplayer: true, npcs: true, mobs: true, combat: true,
    particles: true, parallax: false, vfx: true, simulation: false,
    ui: true, hud: true, chat: false, actionBar: true,
    mapBanner: false, playerList: false, debugToolbox: false,
    quickMenu: true, targetInfo: true, connectionStatus: false,
    editor: false, inspector: false, debugOverlay: false,
    miniStatsDisplay: false, playerInfoPanel: true,
    allowAnyAbilityEquip: true,
  },
  // Clean social-hub viewport. Used by `make tunnel-lobby` — friends join via
  // the h2000-qa tunnel to hang out / stream. Keeps essentials (HUD, chat,
  // action bar, quick-menu, multiplayer, combat so abilities still work) and
  // suppresses every dev / diagnostic / debug surface (contract dashboards,
  // debug toolbox + overlay, inspector, map banners, player list side panel,
  // mini-stats, player-info panel, target info, connection toasts, editor).
  // mobs: true — lobby tools (LobbyToolsPanel) opt-in spawn training dummies /
  // weak pigs / companion bots; without MobSystem registered, those server
  // spawns are invisible on the client. The dj-live-lobby map has no zone
  // file, so zero zone-mob spawns: enabling MobSystem doesn't auto-populate.
  // npcs stays false — the dj-live-lobby map has no quest NPCs.
  'live-lobby': {
    multiplayer: true, npcs: false, mobs: true, combat: true,
    particles: true, parallax: true, vfx: true, simulation: false,
    ui: true, hud: true, chat: true, actionBar: true,
    mapBanner: false, playerList: false, debugToolbox: false,
    // targetInfo flipped to true 2026-05-01 — DJ flagged that target
    // frames weren't showing on /brawl /pve /abilitytest (all use
    // live-lobby preset). Targeting system itself works (TARGET_CHANGED
    // events fire, drawTargetHighlight renders the on-sprite outline);
    // the missing piece was just the UI panel mount. Keeps the
    // social-hub clean-viewport intent — TargetInfoPanel only renders
    // when an actual target is selected (transparent otherwise).
    quickMenu: true, targetInfo: true, connectionStatus: false,
    editor: false, inspector: false, debugOverlay: false,
    miniStatsDisplay: false, playerInfoPanel: true,
    allowAnyAbilityEquip: false,
  },
  // Spectator preset for live tournament viewing. Same hide-the-dev-UI
  // discipline as `live-lobby`, but the SimLiveHUD overlay (top-right)
  // is the centerpiece — viewers see top damage / kills / abilities
  // ticking at 1Hz while bots brawl. Combat stays ON so abilities
  // render correctly; mob/aggro immunity for the spectator is a
  // server-side flag (deferred until next slice). For now, the viewer
  // joins as a participant who simply doesn't engage; F-key follow-
  // leader cam keeps them watching the action regardless.
  'sim-spectator': {
    multiplayer: true, npcs: false, mobs: true, combat: true,
    particles: true, parallax: true, vfx: true, simulation: false,
    ui: true, hud: true, chat: true, actionBar: true,
    mapBanner: false, playerList: false, debugToolbox: false,
    quickMenu: true, targetInfo: false, connectionStatus: false,
    editor: false, inspector: false, debugOverlay: false,
    miniStatsDisplay: false, playerInfoPanel: false,
    allowAnyAbilityEquip: false,
  },
  // `debug-tools-only` — the canonical /debug arena preset (2026-05-13).
  // Minimal chrome so the screen is a clean stage for the diagnostic
  // tools DJ toggles via the 🛠 DebugLauncher (bottom-left). Gameplay
  // still works (combat + mobs + vfx + hotbar) so DJ can exercise the
  // server while watching the panels stream data; the launcher itself
  // mounts regardless of preset (it's outside the preset gate in
  // main.tsx).
  //
  // OFF: hud, chat, quickMenu, mapBanner, playerInfoPanel, targetInfo,
  //      debugToolbox, playerList, connectionStatus, debugOverlay.
  // ON:  actionBar (need to cast), multiplayer/combat/mobs/vfx (need the
  //      game to actually run so signals fire), ui (top-level frame).
  'debug-tools-only': {
    multiplayer: true, npcs: false, mobs: true, combat: true,
    particles: true, parallax: true, vfx: true, simulation: false,
    ui: true, hud: false, chat: false, actionBar: true,
    mapBanner: false, playerList: false, debugToolbox: false,
    quickMenu: false, targetInfo: false, connectionStatus: false,
    editor: false, inspector: false, debugOverlay: false,
    miniStatsDisplay: false, playerInfoPanel: false,
    allowAnyAbilityEquip: false,
  },

  // `combat-lab` — /combatlab (src/game/maps/CombatLab.ts). Systems identical
  // to `debug-tools-only`; it exists as its own preset purely so the LAB can
  // carry its own presentation profile (see PRESET_PRESENTATION below) without
  // changing what /debug shows. The lab's readable surface is the arena itself,
  // and half its job is VFX observation — you cannot judge an effect through a
  // wall of per-mob debug text (2026-07-26, DJ: 13 dummies × 6 diagnostic lines
  // collided into one illegible band across the screen).
  'combat-lab': {
    multiplayer: true, npcs: false, mobs: true, combat: true,
    particles: true, parallax: true, vfx: true, simulation: false,
    ui: true, hud: false, chat: false, actionBar: true,
    mapBanner: false, playerList: false, debugToolbox: false,
    quickMenu: false, targetInfo: false, connectionStatus: false,
    editor: false, inspector: false, debugOverlay: false,
    miniStatsDisplay: false, playerInfoPanel: false,
    allowAnyAbilityEquip: false,
  },
};

export const DEFAULT_PRESENTATION_CONFIG: DevPresentationConfig = {
  mobLabelMode: 'full',
  showMobHealthBars: true,
  showPlayerHealthBars: true,
  showMobRespawnTimers: false,
  multiplayerLabelsOverride: null,
  cameraZoom: null,
  chatDefaultExpanded: true,
  showHealthBarNumbers: true,
  showResourceBarNumbers: true,
};

const PRESET_PRESENTATION: Partial<Record<DevPresetName, Partial<DevPresentationConfig>>> = {
  // Demo + live-lobby were 'hidden' historically for clean
  // social-hub / streaming viewport. Flipped 2026-04-28 per DJ — nameplates
  // (mob names + HP bars) default-ON for all characters across all presets,
  // since QA + lobby sessions both want target visibility. Player nameTags
  // are already unconditionally on (see PlayerRenderer.updateNameTag).
  // multiplayerLabelsOverride stays false — that suppresses the noisy
  // debug coord overlay ("LOCAL <sid>\nS (x,y)\nC (x,y)..."), which is
  // diagnostics, not user-facing nameplates.
  demo: {
    mobLabelMode: 'full',
    showMobHealthBars: true,
    multiplayerLabelsOverride: false,
    cameraZoom: 1.1,
  },
  'live-lobby': {
    mobLabelMode: 'full',
    showMobHealthBars: true,
    multiplayerLabelsOverride: false,
    cameraZoom: 1.1,
  },
  // Combat Lab: the stage must stay READABLE — it is a VFX observation surface
  // as much as a measurement one. Keep the one label that carries information
  // (`mobLabelMode: 'full'` renders each dummy's authored name, which on this
  // map IS its ruler reading, e.g. "130px"); drop the diagnostics dump that
  // buried it. `multiplayerLabelsOverride: false` suppresses the 6-line
  // LOCAL/S/C/V/G/AI coord frame per mob — the same suppression demo and
  // live-lobby use, for the same reason. It is an OVERRIDE, not a deletion:
  // `?diag=1` still forces the labels back on for anyone who wants them, which
  // is how every other debug overlay here gates itself.
  // `showHealthBarNumbers: false` kills "999999/999999" over thirteen dummies
  // (the HP BAR stays — that is the hit feedback a combat lab needs).
  'combat-lab': {
    mobLabelMode: 'full',
    // Health bars OFF here specifically because the lab's dummies carry
    // 999,999 HP: a 48px bar can never visibly move for a ~50 damage hit, so
    // it is a row of thirteen permanently-full green bars — pure clutter that
    // conveys nothing. Hit feedback on this stage is the floating damage
    // number (and the cone overlay), both of which stay.
    showMobHealthBars: false,
    showHealthBarNumbers: false,
    showMobRespawnTimers: false,
    multiplayerLabelsOverride: false,
  },
  // Spectator: zoomed slightly out for wide tournament view, no debug
  // labels, no mob health bars (the SimLiveHUD shows aggregates anyway).
  'sim-spectator': {
    mobLabelMode: 'hidden',
    showMobHealthBars: false,
    showHealthBarNumbers: false,
    showResourceBarNumbers: false,
    multiplayerLabelsOverride: false,
    cameraZoom: 0.85,
  },
};

/**
 * Per-preset default map override.
 * Allows ?preset=1v1-pig to load the 1v1-pig map automatically.
 * Falls back to DevConfig.defaultMap (resolved at devConfig.ts ~:499) if not set.
 */
const PRESET_DEFAULT_MAPS: Partial<Record<DevPresetName, string>> = {
  '1v1-pig': '1v1-pig',
  'arena': 'ability-test-arena',
  'editor': 'empty-canvas',
  'live-lobby': 'stormbreak_cliffs',
  'sim-spectator': 'brawl-arena',
};

export function resolveDevPresetName(urlSearch?: string): DevPresetName {
  let presetName: DevPresetName = 'live-lobby';

  // 1. VITE_PRESET env var — build-time default (Vite static-replaces
  //    `process.env.VITE_PRESET`; the try/catch guards non-Vite hosts /
  //    ts-jest where process.env may be absent). Matches the environment.ts
  //    pattern for VITE_* overrides. Less specific than a URL param, so the
  //    URL ?preset= below still wins.
  try {
    const envPreset = typeof process !== 'undefined' ? process.env?.VITE_PRESET : undefined;
    if (envPreset && envPreset in PRESET_SYSTEMS) {
      presetName = envPreset as DevPresetName;
    }
  } catch {
    // Non-Vite host without process.env replacement — ignore.
  }

  // 2. ?preset= URL param — most specific, overrides the env var.
  try {
    const search = urlSearch ?? bootSearch();
    const urlPreset = search ? new URLSearchParams(search).get('preset') : null;
    if (urlPreset && urlPreset in PRESET_SYSTEMS) {
      presetName = urlPreset as DevPresetName;
    }
  } catch {
    // Ignore malformed URLs in test and boot environments.
  }

  return presetName;
}

export function getSystemsForPreset(presetName: DevPresetName): DevSystemsConfig {
  return { ...PRESET_SYSTEMS[presetName] };
}

export function getPresentationForPreset(presetName: DevPresetName): DevPresentationConfig {
  return {
    ...DEFAULT_PRESENTATION_CONFIG,
    ...(PRESET_PRESENTATION[presetName] || {}),
  };
}

const activePresetName = resolveDevPresetName();
console.log(`[DevConfig] Active preset: ${activePresetName.toUpperCase()}`);

/**
 * Default skipSplash/skipLogin to true on EVERY tier so the bare URL
 * lands straight on the character roster — no splash / login /
 * landing click-through. Covers localhost, PTR, QA tunnels, AND
 * h2000-prod. DJ's directive 2026-05-02: "always skipping that for
 * now and for our backend and stuff. unless our diagnostics."
 *
 * Server-side enforcement is the actual gate:
 *   - `/api/auth/dev/issue` returns 410 Gone when H2000_ENV=prod, so
 *     the client's auto-skip silently fails on prod and falls through
 *     to the (Twitch-only) AuthScreen.
 *   - On dev/staging it returns a player-tier JWT and the user
 *     auto-spawns into character_select.
 *
 * The `/diagnostic` route is its own React entrypoint (main.tsx),
 * doesn't mount `useAuth` / `PreGameFlow`, and runs its own
 * Twitch-session probe. So "skip everywhere" doesn't reach it —
 * diagnostics keeps real auth automatically.
 *
 * Opt out per session via `?skipSplash=0&skipLogin=0` if you need to
 * test the splash → login flow on PTR.
 */
const devDefaultSkipFrontFacing = true;

/**
 * Read boolean URL params for debug toggles.
 * The pretest loader passes these so the game auto-skips splash/login.
 */
function readUrlBool(param: string, fallback: boolean): boolean {
  try {
    const value = bootParam(param);
    if (value === null) return fallback;
    return value === '1' || value === 'true';
  } catch {
    return fallback;
  }
}

/**
 * Read a string URL param with fallback. Used for dev-character overrides
 * (?class=mage, ?name=FloorHero) so tests can drop straight into gameplay
 * with a specific class without clicking through character creation.
 */
function readUrlString(param: string, fallback: string): string {
  try {
    return bootParam(param) ?? fallback;
  } catch {
    return fallback;
  }
}

function readUrlNumber(param: string, fallback: number): number {
  try {
    const raw = bootParam(param);
    if (raw === null) return fallback;
    const num = Number(raw);
    return Number.isFinite(num) ? num : fallback;
  } catch {
    return fallback;
  }
}

/** Keep the dev traversal override inside the range used by noclip flight. */
export function normalizeSpeedMultiplier(multiplier: number): number {
  if (!Number.isFinite(multiplier)) return 1.0;
  return Math.max(0.1, Math.min(10, multiplier));
}

/**
 * Playtest rig — declarative test-surface config so I (Claude / agent)
 * can set up a deterministic debug viewport from a config file alone,
 * without DJ manually opening windows + flipping checkboxes.
 *
 * On mount in dev / staging builds, <PlaytestRigApplier> reads this and:
 *   - Opens every tool in `toolsToOpen` (data-testid `debugtool_<id>`).
 *   - Flips FrameJitterTracker to ENABLED if `enableFrameJitterTracking`.
 *   - Flips ReactRenderTracker to ENABLED if `enableReactRenderTracking`.
 *
 * Idempotent — every reload reapplies the rig, so accidentally closing
 * a window during play just gets re-opened on next refresh. Set
 * `enabled: false` (or null the whole field) to disable.
 *
 * Production builds skip the applier (gated on isDevMode).
 *
 * Tool IDs come from the window registry (ui/framework/registry.ts → WINDOW_REGISTRY) —
 * at the time of writing: latency, netTraffic, netobsSparkline, netSpike,
 * tickBadge, statusBar, debugInfo, teleport, eventRate, lagRootCause,
 * positionTracker, frameJitter, smoothnessDashboard.
 */
export interface PlaytestRig {
  enabled: boolean;
  /** Human-readable label for the rig, surfaced in logs on apply. */
  label?: string;
  /** Tool IDs (from the kit registry) to force open. */
  toolsToOpen: string[];
  /** Auto-flip the Frame Jitter rAF analyzer to TRACKING. */
  enableFrameJitterTracking: boolean;
  /** Auto-flip the React render-rate tracker to TRACKING. */
  enableReactRenderTracking: boolean;
  /** Auto-flip the SnapshotStatsTracker (inter-client interpolation health)
   *  to TRACKING. Default off; set on smoothness rigs. */
  enableSnapshotStats?: boolean;
  /**
   * Delay (ms) before trackers flip ON. Tools open immediately but
   * trackers wait so cold-start / first-paint frames don't pollute the
   * snapshot. Default 10000 (10s). Lower for synthetic tests that need
   * faster feedback; raise if the page-load cost on slow hardware is
   * spilling into the measurement window.
   */
  trackerEnableDelayMs?: number;
}

// Active rig is selected by the rig library at
// `src/config/playtest-rigs/`. Tier A's `dj_h2000_rig_set` MCP rewrites
// `ACTIVE_RIG_NAME` in `playtest-rigs/index.ts` to switch rigs without
// editing this file. Set NO_RIG (or null) to disable auto-application.
import { getActiveRig } from '../playtest-rigs';

export const DevConfig = {
  /** The active preset name (e.g., 'demo', 'multiplayer', 'gameplay') */
  activePreset: activePresetName,

  /** See PlaytestRig — resolved from src/config/playtest-rigs/index.ts. */
  playtestRig: getActiveRig() as PlaytestRig | null,

  // ============================================================
  // DEV CHARACTER (Auto-login when skipLogin is true)
  // ============================================================

  /**
   * Default character class for dev login: warrior | mage | hunter | paladin | rogue.
   * Overridable at boot via `?class=<name>` URL param — tests use this to drop
   * straight into gameplay with a specific class (no character creation click-through).
   */
  devCharacterClass: readUrlString('class', 'paladin'),

  /** Default character name for dev login. Overridable via `?name=<name>`. */
  devCharacterName: readUrlString('name', 'ShadowStriker'),

  /** `?exactName=1` keeps `?name=` without the anti-collision `_NNN` suffix (listing-shots.mjs). */
  devExactName: readUrlBool('exactName', false),

  skipLogin: readUrlBool('skipLogin', devDefaultSkipFrontFacing),

  /** Skip splash screen and auto-start game */
  skipSplash: readUrlBool('skipSplash', devDefaultSkipFrontFacing),

  // ============================================================
  // GAME STARTUP
  // ============================================================

  /**
   * Default map to load when starting the game. Resolution priority:
   *   1. URL `?map=<id>`        — explicit override (used by `make tile`)
   *   2. PRESET_DEFAULT_MAPS    — per-preset default (e.g. editor → empty-canvas)
   *   3. 'stormbreak_cliffs'    — canonical default (tempest biome showcase)
   *
   * Without step 1, `?preset=editor&map=minimal-tile` would load empty-canvas
   * because NetworkManager passes `devConfig.defaultMap` to joinOrCreate —
   * URL param otherwise bypassed.
   */
  defaultMap: (readUrlString('map', '') || PRESET_DEFAULT_MAPS[activePresetName] || 'stormbreak_cliffs') as MapId,

  /** Force default map even if dev-map exists */
  forceDefaultMap: false,

  /** Zen Mode — minimal boot (player + flat ground only) */
  zenMode: false,

  // ============================================================
  // SYSTEM PRESETS (VITE_PRESET env var or ?preset= URL param)
  // Valid: minimal | editor | gameplay | multiplayer | full | demo
  // ============================================================
  systems: {
    ...getSystemsForPreset(activePresetName),
    // Allow pretest loader to override overlay toggles via URL params
    debugToolbox: readUrlBool('debugToolbox', getSystemsForPreset(activePresetName).debugToolbox),
    debugOverlay: readUrlBool('debugOverlay', getSystemsForPreset(activePresetName).debugOverlay),
    connectionStatus: readUrlBool('connectionStatus', getSystemsForPreset(activePresetName).connectionStatus),
    miniStatsDisplay: readUrlBool('miniStatsDisplay', getSystemsForPreset(activePresetName).miniStatsDisplay),
    playerInfoPanel: readUrlBool('playerInfoPanel', getSystemsForPreset(activePresetName).playerInfoPanel),
  },

  // Presentation flags — preset-specific visual cleanup for demo capture.
  presentation: getPresentationForPreset(activePresetName),

  // ============================================================
  // GAMEPLAY SETTINGS
  // ============================================================

  /** Abilities phase through NPCs (don't damage them) */
  abilitiesPhaseThroughNPCs: false,

  /** God mode - player takes no damage */
  godMode: readUrlBool('godMode', false),

  /** Noclip flight mode - player flies and phases through terrain */
  noclip: readUrlBool('noclip', false),

  /** Speed multiplier for fast traversal */
  speedMultiplier: normalizeSpeedMultiplier(readUrlNumber('speed', 1.0)),

  /** Instant kill - player kills enemies in one hit */
  instantKill: false,

  /** No cooldowns on abilities */
  noCooldowns: false,

  /** Infinite mana */
  infiniteMana: false,

  /** Show damage numbers floating above entities */
  showDamageNumbers: true,

  /** Debug Floating Combat Text — per-system toggles */
  debugFCT: activePresetName === '1v1-pig' ? {
    cast: true,      // Ability cast pipeline (slot→resolve→mana→gcd→cd→execute)
    vfx: true,       // VFX effect spawn (effectId→registry→scene→primitives)
    combat: true,    // Combat pipeline (bridge→server echo→damage applied)
    network: true,   // Network messages (sent/received with timing)
  } : {
    cast: true,      // Ability cast pipeline (slot→resolve→mana→gcd→cd→execute)
    vfx: false,      // VFX effect spawn (effectId→registry→scene→primitives)
    combat: true,    // Combat pipeline (bridge→server echo→damage applied)
    network: false,  // Network messages (sent/received with timing) — noisy
  },

  // ============================================================
  // DEBUG FLAGS
  // ============================================================

  /** Show hitboxes for entities and platforms */
  showHitboxes: readUrlBool('showHitboxes', false),

  /** Show FPS counter in game */
  showFPS: readUrlBool('showFPS', false),

  /** Enable verbose console logging - set to false for reduced log spam */
  verboseLogging: readUrlBool('verboseLogging', false),

  /**
   * Show editor overlay by default on game start. Defaults to false so the
   * user-facing experience isn't cluttered with editor UI. Overridable via
   * `?editorOn=1` URL param — `make tile` passes it so DJ's editor-QA
   * session boots straight into the painting surface.
   */
  editorOverlayOnByDefault: readUrlBool('editorOn', false),

  /** Enable remote logging to the server */
  enableRemoteLogging: true,

  // ============================================================
  // LOBBY TOOLS (player-facing "play with the lobby" panel)
  // ============================================================

  /**
   * Master kill-switch for the Lobby Tools panel.
   *
   * When false:
   *   - QuickMenu hides the 🧰 Lobby Tools button everywhere, even on
   *     lobby maps.
   *   - Server skips registering `LobbyToolsHandler`, so any
   *     `lobby_tool` message is silently dropped.
   *
   * When true, map-gate still applies — tools only work on maps listed
   * in `LOBBY_TOOLS_MAP_REGISTRY` (see `src/shared/constants/LobbyTools.ts`).
   *
   * Override per session with `?lobbyTools=0` in the URL.
   */
  lobbyToolsEnabled: readUrlBool('lobbyTools', true),

  // ============================================================
  // LOGGING CATEGORIES
  // ============================================================
  logging: activePresetName === '1v1-pig' ? {
    boot: true,           // Boot sequence - startup diagnostics
    registry: true,       // Service registry - track what's loaded
    map: true,            // Map loading - see map init
    camera: false,        // Camera movement - too noisy
    physics: false,       // Physics updates - too noisy
    input: false,         // Input events - too noisy
    entity: true,         // Entity spawning - see pig mom spawn
    network: true,        // Network messages - see combat sync
    teleporter: true,     // Teleporter events - rare and important
    terrain: false,       // Terrain generation - noisy
  } : {
    boot: true,           // Boot sequence - keep on for startup diagnostics
    registry: false,      // Service registry - noisy
    map: true,            // Map loading - important
    camera: false,        // Camera movement - can be noisy
    physics: false,       // Physics updates - very noisy
    input: false,         // Input events - very noisy
    entity: false,        // Entity spawning - can be noisy
    network: false,       // Network messages - very noisy
    teleporter: true,     // Teleporter events - rare and important
    terrain: false,       // Terrain generation - noisy
  },

  // ============================================================
  // NETWORK
  // ============================================================

  // WebSocket / HTTP URLs for Colyseus + REST. These are hostname-
  // derived at runtime (window.location.hostname) so they work on any
  // dev origin (localhost:3001, 127.0.0.1, LAN IP for device-testing).
  // SSR fallback string literals removed 2026-05-02 — they bundled
  // 'localhost' into production builds without ever being executed
  // (we're a browser-only Vite client). For prod URLs, see environment.ts.
  serverUrl: typeof window !== 'undefined' ? `ws://${window.location.hostname}:2567` : '',
  apiUrl: typeof window !== 'undefined' ? `http://${window.location.hostname}:2567/api` : '',
  autoReconnect: true,
  connectionTimeout: 10000,

  // ============================================================
  // AUDIO
  // ============================================================

  masterVolume: 0.5,
  musicVolume: 0.3,
  sfxVolume: 0.7,
  muteAudio: false,

  // ============================================================
  // GAME RESOLUTION
  // ============================================================
  game: {
    width: 1280,
    height: 720,
    backgroundColor: '#000000',
    parent: 'phaser-game-container',
  },
};

// Type exports
// Testing-map IDs are a dynamic set loaded from `src/game/maps/testing/*.toml`
// at boot (see `src/game/maps/testing/loader.ts`). Adding a new `.toml`
// doesn't require amending this union — specs pass `map: 'testing-<id>'`
// directly. The canonical IDs included here are the gameplay + template
// surface only.
export type MapId = 'starter-map' | 'dev-map' | 'tutorial-island' | 'multiplayer-observe' | '1v1-pig' | 'pig-testring' | 'ability-test-arena' | 'empty-canvas' | 'minimal-tile' | 'dj-live-lobby' | 'brawl-arena' | 'pve-arena' | 'ability-range-test' | `testing-${string}`;
export type DevCharacterClassType = 'warrior' | 'mage' | 'hunter' | 'paladin' | 'rogue' | 'shaman' | 'priest' | 'warlock' | 'druid';

export function getDevConfig() {
  return DevConfig;
}

export function printDevConfig() {
  console.log('=== Dev Config ===');
  console.log(JSON.stringify(DevConfig, null, 2));
}
